import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Plus, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import Modal from "../components/Modal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Field, FieldLabel, FieldMessage } from "../components/ui/field";
import { DataValue, PassiveBadge } from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableRefreshingRow,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnSelectFilter,
  ColumnTextFilter,
  useSearchFilterState,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { formatApiError, getServiceErrorPayload } from "../lib/api";
import {
  createMasterData,
  listMasterData,
  setMasterDataActive,
  updateMasterData,
} from "../lib/masterDataApi";
import useUnsavedChangesGuard from "../hooks/useUnsavedChangesGuard";

const CONFIG = {
  technicians: {
    singular: "Technician",
    plural: "Technicians",
    resource: "technicians",
  },
  "device-models": {
    singular: "Device Model",
    plural: "Device Models",
    resource: "device-models",
  },
};

const MASTER_DATA_TABLE_GEOMETRY = Object.freeze([
  { key: "name", kind: "fixed", width: 240 },
  { key: "notes", kind: "fill", minWidth: 360 },
  { key: "status", kind: "fixed", width: 120 },
  { key: "actions", kind: "utility", width: 104 },
]);

function normalizeMasterDataDraft(name, notes) {
  return {
    name: String(name || "").trim(),
    notes: String(notes || "").trim() || null,
  };
}

function MasterDataModal({ config, record, onClose, onSaved }) {
  const [name, setName] = useState(record?.name || "");
  const [notes, setNotes] = useState(record?.notes || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const baselineRef = useRef(normalizeMasterDataDraft(record?.name, record?.notes));

  useEffect(() => {
    baselineRef.current = normalizeMasterDataDraft(record?.name, record?.notes);
  }, [record]);

  const isDirty = JSON.stringify(normalizeMasterDataDraft(name, notes)) !== JSON.stringify(baselineRef.current);
  const leaveGuard = useUnsavedChangesGuard(isDirty && !busy);
  const requestClose = () => leaveGuard.requestLeave(onClose);

  async function submit(event) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(`${config.singular} name is required.`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = { name: trimmedName, notes: notes.trim() || null };
      const response = record
        ? await updateMasterData(config.resource, record.id, payload)
        : await createMasterData(config.resource, payload);
      baselineRef.current = normalizeMasterDataDraft(payload.name, payload.notes);
      toast.success(response.message);
      onSaved();
    } catch (err) {
      const apiError = getServiceErrorPayload(err);
      setError(apiError?.field_errors?.name || formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${record ? "Edit" : "Add"} ${config.singular}`}
      onClose={requestClose}
      busy={busy}
      size="sm"
      testid="master-data-modal"
    >
      <form onSubmit={submit} className="space-y-5">
        <Field>
          <FieldLabel htmlFor="master-data-name" required>Name</FieldLabel>
          <Input
            id="master-data-name"
            className="form-control"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "master-data-name-error" : undefined}
            data-testid="master-data-name"
          />
          <FieldMessage id="master-data-name-error" role={error ? "alert" : undefined} data-testid="master-data-form-error">
            {error}
          </FieldMessage>
        </Field>
        <Field>
          <FieldLabel htmlFor="master-data-notes">Notes</FieldLabel>
          <Textarea
            id="master-data-notes"
            className="form-control form-control--textarea resize-y"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            data-testid="master-data-notes"
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="cancel" onClick={requestClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="success" loading={busy} loadingLabel="Saving..." data-testid="master-data-save">
            Save
          </Button>
        </div>
      </form>
      {leaveGuard.dialog}
    </Modal>
  );
}

export default function MasterDataPage({ type }) {
  const config = CONFIG[type];
  const [records, setRecords] = useState([]);
  const [filterDrafts, setFilterDrafts] = useState({ name: "", notes: "", status: "" });
  const [editing, setEditing] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusBusy, setStatusBusy] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const searchFilters = useSearchFilterState({ onPageReset: () => setPage(1) });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const firstResponse = await listMasterData(config.resource, { page: 1, page_size: 100 });
      const allRecords = Array.isArray(firstResponse.data) ? [...firstResponse.data] : [];
      const totalPages = Math.max(1, firstResponse.pagination?.total_pages || 0);
      for (let apiPage = 2; apiPage <= totalPages; apiPage += 1) {
        const response = await listMasterData(config.resource, { page: apiPage, page_size: 100 });
        if (Array.isArray(response.data)) allRecords.push(...response.data);
      }
      setRecords(allRecords);
    } catch (err) {
      setRecords([]);
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [config.resource]);

  useEffect(() => {
    load();
  }, [load]);

  async function changeStatus(record) {
    setStatusBusy(record.id);
    try {
      const response = await setMasterDataActive(config.resource, record.id, !record.is_active);
      toast.success(response.message);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStatusBusy("");
    }
  }

  function saved() {
    setEditing(undefined);
    load();
  }

  const appliedFilters = searchFilters.state.columnFilters;
  const filteredRecords = useMemo(() => records.filter((record) => {
    if (appliedFilters.name && !String(record.name || "").toLowerCase().includes(appliedFilters.name.toLowerCase())) return false;
    if (appliedFilters.notes && !String(record.notes || "").toLowerCase().includes(appliedFilters.notes.toLowerCase())) return false;
    if (appliedFilters.status && String(record.is_active) !== appliedFilters.status) return false;
    return true;
  }), [appliedFilters, records]);
  const totalPages = Math.ceil(filteredRecords.length / pageSize);
  const pageRecords = filteredRecords.slice((page - 1) * pageSize, page * pageSize);
  const hasAppliedFilters = Object.values(appliedFilters).some(Boolean);

  function updateFilterDraft(name, value) {
    setFilterDrafts((current) => ({ ...current, [name]: value }));
  }

  function clearAllFilters() {
    setFilterDrafts({ name: "", notes: "", status: "" });
    searchFilters.clearAll();
  }

  return (
    <OrdinaryPageBody data-testid={`master-data-${type}`}>
      <OrdinaryPageHeader
        title={config.plural}
        actions={<Button onClick={() => setEditing(null)} data-testid="master-data-add">
          <Plus className="mr-2 h-4 w-4" /> Add {config.singular}
        </Button>}
        data-testid="master-data-page-header"
      />

      <ListTableShell data-testid="master-data-panel">
        <ListTableViewport>
          <ListTable geometry={MASTER_DATA_TABLE_GEOMETRY} data-testid="master-data-table">
            <thead>
              <tr>
                <th>{config.singular}</th>
                <th>Notes</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
              <ColumnFilterRow className="bg-white">
                <ColumnFilterCell><ColumnTextFilter value={filterDrafts.name} onValueChange={(value) => updateFilterDraft("name", value)} onApply={(value) => searchFilters.applyColumnFilter("name", value)} placeholder="Filter…" aria-label={`Filter ${config.singular.toLowerCase()} name`} data-testid="master-data-filter-name" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnTextFilter value={filterDrafts.notes} onValueChange={(value) => updateFilterDraft("notes", value)} onApply={(value) => searchFilters.applyColumnFilter("notes", value)} placeholder="Filter…" aria-label={`Filter ${config.singular.toLowerCase()} notes`} data-testid="master-data-filter-notes" /></ColumnFilterCell>
                <ColumnFilterCell><ColumnSelectFilter value={filterDrafts.status} onValueChange={(value) => updateFilterDraft("status", value)} onApply={(value) => searchFilters.applyColumnFilter("status", value)} placeholder="All" options={[{ value: "true", label: "Active" }, { value: "false", label: "Inactive" }]} aria-label={`Filter ${config.singular.toLowerCase()} status`} data-testid="master-data-filter-status" /></ColumnFilterCell>
                <ColumnFilterCell className="align-middle" aria-label="Actions filter controls">
                  <div className="flex h-7 w-full items-center justify-end">
                    {hasAppliedFilters ? <ClearAllFilters onClick={clearAllFilters} data-testid="master-data-filter-clear-all" /> : null}
                  </div>
                </ColumnFilterCell>
              </ColumnFilterRow>
            </thead>
            <tbody>
              <ListTableRefreshingRow active={loading && records.length > 0} colSpan={4} />
              {loading && records.length === 0 ? (
                <ListTableState colSpan={4} kind="loading">Loading...</ListTableState>
              ) : error ? (
                <ListTableState colSpan={4} kind="error" onRetry={load}>{error}</ListTableState>
              ) : pageRecords.length === 0 ? (
                <ListTableState colSpan={4} kind={hasAppliedFilters ? "filtered-empty" : "empty"}>
                  {hasAppliedFilters ? "No records match the current filters." : "No records found."}
                </ListTableState>
              ) : null}
              {!error ? pageRecords.map((record) => (
                <tr key={record.id} data-testid="master-data-row">
                  <td className="max-w-[320px] font-medium text-zinc-900"><DataValue value={record.name} /></td>
                  <td className="max-w-xl whitespace-pre-wrap text-zinc-600"><DataValue value={record.notes} /></td>
                  <td>
                    <PassiveBadge
                      tone={record.is_active ? "success" : "neutral"}
                      data-testid={`master-data-status-${record.id}`}
                    >
                      {record.is_active ? "Active" : "Inactive"}
                    </PassiveBadge>
                  </td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <Button size="row-icon" variant="edit" title="Edit" aria-label={`Edit ${record.name}`} onClick={() => setEditing(record)}>
                        <Pencil />
                      </Button>
                      <Button
                        size="row-icon"
                        title={record.is_active ? "Deactivate" : "Activate"}
                        aria-label={`${record.is_active ? "Deactivate" : "Activate"} ${record.name}`}
                        variant={record.is_active ? "transfer-remove" : "restore"}
                        disabled={statusBusy === record.id}
                        onClick={() => changeStatus(record)}
                      >
                        {record.is_active ? <PowerOff /> : <Power />}
                      </Button>
                    </div>
                  </td>
                </tr>
              )) : null}
            </tbody>
          </ListTable>
        </ListTableViewport>
        <ListPagination
          page={page}
          pageSize={pageSize}
          total={filteredRecords.length}
          totalPages={totalPages}
          loading={loading}
          onPrevious={() => setPage((current) => Math.max(1, current - 1))}
          onNext={() => setPage((current) => current + 1)}
          onPageSizeChange={(nextPageSize) => { setPageSize(nextPageSize); setPage(1); }}
          pageSizeAriaLabel={`${config.plural} rows per page`}
          summaryTestId="master-data-page-summary"
          pageTestId="master-data-page-number"
          previousTestId="master-data-previous-page"
          nextTestId="master-data-next-page"
        />
      </ListTableShell>

      {editing !== undefined ? (
        <MasterDataModal
          config={config}
          record={editing}
          onClose={() => setEditing(undefined)}
          onSaved={saved}
        />
      ) : null}
    </OrdinaryPageBody>
  );
}
